import React from 'react';
import { Clock, Wallet, CalendarCheck, Flame } from 'lucide-react';
import { activeEngineers, BOOKING_DEPOSIT, MIN_SESSION_HOURS, BALANCE_DUE_NOTE } from '../lib/bookingRules';
import ReviewStrip from './ReviewStrip';

const FACTS = [
  { icon: Clock, label: `${MIN_SESSION_HOURS}-hour minimum`, sub: 'on hourly sessions' },
  { icon: Wallet, label: `$${BOOKING_DEPOSIT} deposit`, sub: 'holds your spot - or pay in full' },
  { icon: CalendarCheck, label: 'Balance at the studio', sub: 'paid on session day' },
];

export const RecordingPricing = () => (
  <section id="pricing" data-testid="recording-pricing" className="relative py-16 bg-black">
    <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
      <p className="text-xs uppercase tracking-[0.25em] text-purple-400 mb-3">Recording Session Pricing</p>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-white max-w-xl">Hourly rates, no surprises. See your total before you check out.</h2>
        <a href="/book" data-testid="pricing-book-btn" className="self-start lg:self-auto inline-flex items-center rounded-full bg-gradient-to-r from-purple-600 to-blue-600 px-7 py-3.5 font-bold text-white hover:scale-105 transition-transform">
          BOOK A RECORDING SESSION
        </a>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mb-8">
        {FACTS.map(({ icon: Icon, label, sub }) => (
          <div key={label} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <Icon size={20} className="text-purple-400 flex-shrink-0" />
            <div>
              <div className="text-white font-semibold text-sm">{label}</div>
              <div className="text-gray-400 text-xs">{sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-purple-500/20">
        <table data-testid="pricing-table" className="w-full text-sm">
          <thead className="bg-purple-950/40 text-left text-gray-300">
            <tr>
              <th className="px-3 sm:px-4 py-3 font-semibold">Engineer</th>
              <th className="px-3 sm:px-4 py-3 font-semibold">Rate</th>
              <th className="px-3 sm:px-4 py-3 font-semibold">2 hrs <span className="hidden sm:inline font-normal text-gray-500">(minimum)</span></th>
              <th className="px-3 sm:px-4 py-3 font-semibold">
                <span className="inline-flex items-center gap-1">3 hrs <Flame size={14} className="text-orange-400" /><span className="hidden sm:inline font-normal text-orange-300">Most popular</span></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {activeEngineers.map((e) => (
              <tr key={e.id} data-testid={`pricing-row-${e.id}`} className="border-t border-white/5">
                <td className="px-3 sm:px-4 py-3 text-white font-medium">{e.name}</td>
                <td className="px-3 sm:px-4 py-3 text-gray-300 whitespace-nowrap">${e.hourlyRate}/hr</td>
                <td className="px-3 sm:px-4 py-3 text-gray-300">${e.hourlyRate * MIN_SESSION_HOURS}</td>
                <td className="px-3 sm:px-4 py-3 text-green-400 font-semibold">${e.bundleRate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {BALANCE_DUE_NOTE} Limited-time promotions and package deals are shown separately and apply automatically at checkout while active.
      </p>

      <ReviewStrip className="mt-10" />
    </div>
  </section>
);

export default RecordingPricing;
